import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Container, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { getRuntimeConfig } from '../../app/runtimeConfig';
import { useSession } from '../../app/session';

export default function LoginScreen() {
  const runtime = getRuntimeConfig();
  const { authConfig, signInWithCognito, signInWithDebug, authError } = useSession();
  const [tenantId, setTenantId] = useState('tenant-sandbox');
  const [role, setRole] = useState<'platform-admin' | 'tenant-admin' | 'operator' | 'viewer'>('platform-admin');
  const [email, setEmail] = useState('operator@sandbox.local');
  const [loading, setLoading] = useState<'debug' | 'oauth' | ''>('');
  const [localError, setLocalError] = useState('');

  const submitDebug = async () => {
    setLoading('debug');
    setLocalError('');
    try {
      await signInWithDebug({
        tenantId,
        role,
        email,
      });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to sign in.');
    } finally {
      setLoading('');
    }
  };

  const submitHostedLogin = async () => {
    setLoading('oauth');
    setLocalError('');
    try {
      await signInWithCognito();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to start hosted sign-in.');
      setLoading('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, #efe3c2, #d6e3eb 56%, #f8fafb 100%)', py: 8 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4, boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)' }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box>
                <Chip label={`Stage: ${runtime.stage}`} sx={{ mb: 2, fontWeight: 700 }} />
                <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>
                  Operator Console
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
                  Sprint 6 completes the auth cutover. Production uses Cognito-hosted login with OAuth PKCE,
                  while sandbox keeps the debug sign-in path so the operator console can still exercise the
                  protected API quickly during validation.
                </Typography>
              </Box>

              {authError ? <Alert severity="warning">{authError}</Alert> : null}
              {localError ? <Alert severity="error">{localError}</Alert> : null}

              {authConfig?.cognitoDomain ? (
                <Button variant="contained" size="large" onClick={() => void submitHostedLogin()} disabled={loading !== ''}>
                  {loading === 'oauth' ? 'Redirecting...' : 'Sign In With Cognito'}
                </Button>
              ) : null}

              {authConfig?.debugAuthEnabled ? (
                <>
                  <TextField label="Tenant ID" value={tenantId} onChange={(event) => setTenantId(event.target.value)} fullWidth />
                  <TextField
                    select
                    label="Role"
                    value={role}
                    onChange={(event) => setRole(event.target.value as typeof role)}
                    fullWidth
                  >
                    <MenuItem value="platform-admin">Platform Admin</MenuItem>
                    <MenuItem value="tenant-admin">Tenant Admin</MenuItem>
                    <MenuItem value="operator">Operator</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </TextField>
                  <TextField label="Email" value={email} onChange={(event) => setEmail(event.target.value)} fullWidth />

                  <Button variant="outlined" size="large" onClick={() => void submitDebug()} disabled={loading !== ''}>
                    {loading === 'debug' ? 'Signing In...' : runtime.stage === 'sandbox' ? 'Enter Sandbox Console' : 'Use Debug Login'}
                  </Button>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
